import React, { useEffect, useRef, useState } from 'react';
import { IDocumentPayload, IGridPayload, IGridItem } from 'services/interface';
import { useTracing } from 'hooks/useTracing';
import './BillMessage.scss';

interface GridMessageProps {
    grid: IGridPayload;
    disabled?: boolean;
    onSubmit: (item: IGridItem) => void;
}

export const GridMessage: React.FC<GridMessageProps> = ({ grid, disabled = false, onSubmit }) => {
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const [locked, setLocked] = useState(false);
    const selectedItem = grid.items.find(item => item.value === selectedValue);
    const columns = Math.max(1, Math.min(6, grid.columns || 1));

    const confirmLabel = (grid.confirm?.label || 'Confirm').replace(
        /\{label\}/g,
        selectedItem?.label || ''
    );

    const selectItem = (item: IGridItem) => {
        if (!disabled && !locked && !item.disabled) {
            setSelectedValue(item.value);
            if (!grid.confirm) {
                setLocked(true);
                onSubmit(item);
            }
        }
    };

    return (
        <div className="bill-grid" aria-label={grid.title || 'Select an option'}>
            {grid.title && (
                <div className="bill-grid__title">
                    {grid.icon === 'calendar' && <span aria-hidden="true">📅</span>}
                    <span>{grid.title}</span>
                </div>
            )}
            <div
                className="bill-grid__items"
                style={{ '--bill-grid-columns': columns } as React.CSSProperties}
            >
                {grid.items.map(item => (
                    <button
                        key={item.value}
                        type="button"
                        className={`bill-grid__item${selectedValue === item.value ? ' bill-grid__item--selected' : ''}`}
                        disabled={disabled || locked || item.disabled}
                        aria-pressed={selectedValue === item.value}
                        onClick={() => selectItem(item)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
            {grid.confirm && (
                <button
                    type="button"
                    className="bill-grid__confirm"
                    disabled={disabled || locked || !selectedItem}
                    onClick={() => {
                        if (!selectedItem) return;
                        setLocked(true);
                        onSubmit(selectedItem);
                    }}
                >
                    {confirmLabel}
                </button>
            )}
        </div>
    );
};

interface DocumentMessageProps {
    document: IDocumentPayload;
    onUrlAvailabilityChange?: (available: boolean) => void;
}

const getUrlFailureReason = (url: string | null, expiresAt: string | null): string => {
    if (!url) return 'null';
    if (!expiresAt) return 'load_error';
    return new Date(expiresAt).getTime() <= Date.now() ? 'expired' : 'load_error';
};

const decodeBase64Pdf = (value: string): Blob => {
    const rawBase64 = value.replace(/^data:application\/pdf;base64,/, '').replace(/\s/g, '');
    const binary = window.atob(rawBase64);
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    return new Blob([bytes], { type: 'application/pdf' });
};

export const DocumentMessage: React.FC<DocumentMessageProps> = ({ document, onUrlAvailabilityChange }) => {
    const { trackUserAction } = useTracing();
    const trackUserActionRef = useRef(trackUserAction);
    trackUserActionRef.current = trackUserAction;
    const [sourceUrl, setSourceUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;
        const url = document.url;
        const expiresAt = document.expiresAt;

        const publishFailure = (reason: string) => {
            trackUserActionRef.current('bill_document_rendered', {
                mode: 'base64',
                urlFailureReason: reason
            });
        };

        const loadBase64 = (reason: string) => {
            publishFailure(reason);
            if (!document.base64) {
                if (!cancelled) {
                    setError(true);
                    setLoading(false);
                    onUrlAvailabilityChange?.(false);
                }
                return;
            }

            try {
                objectUrl = URL.createObjectURL(decodeBase64Pdf(document.base64));
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }
                setSourceUrl(objectUrl);
                setError(false);
                setLoading(false);
                onUrlAvailabilityChange?.(false);
            } catch {
                if (!cancelled) {
                    setError(true);
                    setLoading(false);
                    onUrlAvailabilityChange?.(false);
                }
            }
        };

        const loadDocument = async () => {
            setLoading(true);
            setError(false);

            if (!url || !expiresAt || new Date(expiresAt).getTime() <= Date.now()) {
                loadBase64(getUrlFailureReason(url, expiresAt));
                return;
            }

            try {
                const response = await fetch(url);
                if (!response.ok) {
                    loadBase64(`http_${response.status}`);
                    return;
                }

                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }
                setSourceUrl(objectUrl);
                setLoading(false);
                onUrlAvailabilityChange?.(true);
                trackUserActionRef.current('bill_document_rendered', { mode: 'url' });
            } catch {
                loadBase64('load_error');
            }
        };

        loadDocument();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [document, onUrlAvailabilityChange]);

    return (
        <div className="bill-document">
            <div className="bill-document__header">
                <div>
                    <strong>{document.details?.label || document.fileName}</strong>
                    <span>{document.fileName}</span>
                </div>
                <span className="bill-document__size">
                    {Math.ceil(document.sizeBytes / 1024)} KB
                </span>
            </div>

            {loading && <div className="bill-document__status">Loading your bill…</div>}
            {error && <div className="bill-document__status bill-document__status--error">We couldn't display your bill</div>}
            {!loading && !error && sourceUrl && (
                <iframe
                    className="bill-document__preview"
                    title={`Preview of ${document.fileName}`}
                    src={`${sourceUrl}#page=1`}
                />
            )}

            {sourceUrl && !error && (
                <a
                    className="bill-document__download"
                    href={sourceUrl}
                    download={document.fileName}
                >
                    Download PDF
                </a>
            )}
        </div>
    );
};
